<template>
  <div class="rich-text-editor">
    <editor v-model="content" :init="editorConfig" @input="updateContent" />
    <div class="editor-footer">
      <button class="publish-btn" @click="publishContent">发布</button>
      <button class="upload-btn" @click="triggerUpload">
        <i class="fas fa-upload"></i> 上传
      </button>
      <input
        type="file"
        ref="imageInput"
        style="display: none"
        @change="handleImageUpload"
        accept="image/*"
      />
    </div>
  </div>
</template>

<script setup>
import Editor from "@tinymce/tinymce-vue";
import { ref, watch, onMounted, onBeforeUnmount } from "vue";
import axios from "axios";
import { getQiniuToken as apiGetQToken } from "@/api/api";
import { ElMessage, ElLoading } from "element-plus";
import { debounce } from "lodash";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
let isFetchingToken = false; // Token获取锁

const props = defineProps({
  modelValue: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["update:modelValue", "publish"]);
const content = ref(props.modelValue);
const imageInput = ref(null);
const qiniuToken = ref("");
const QINIU_UPLOAD_URL = "https://upload.qiniup.com";
const QINIU_CDN_URL = "http://image.langtaichem.com";

// 获取七牛云 Token
const initQiniu = async () => {
  if (isFetchingToken || qiniuToken.value) return;
  isFetchingToken = true;

  try {
    const response = await apiGetQToken();
    if (response?.data) {
      qiniuToken.value = response.data;
    } else {
      throw new Error("未能获取有效的Token");
    }
  } catch (error) {
    console.error("获取七牛云Token失败:", error);
    ElMessage.error(`上传服务初始化失败: ${error.message || "请稍后重试"}`);
    throw error;
  } finally {
    isFetchingToken = false;
  }
};

// 验证文件是否合法
const validateFile = (file) => {
  if (!file) throw new Error("未选择文件");
  if (!file.type.startsWith("image/")) throw new Error("仅支持图片文件");
  if (file.size > MAX_FILE_SIZE) throw new Error("图片大小不能超过10MB");
  return true;
};

// 规范化文件名
const sanitizeFileName = (filename) => {
  return filename
    .replace(/[^a-zA-Z0-9-_.]/g, "_")
    .replace(/_+/g, "_")
    .slice(0, 100); // 限制文件名长度
};

// 通用上传逻辑
const uploadToQiniu = async (file, blobInfo = null) => {
  try {
    // 验证文件
    validateFile(file);

    // 确保 Token 存在
    if (!qiniuToken.value) {
      await initQiniu();
      if (!qiniuToken.value) throw new Error("上传Token未初始化");
    }

    // 生成唯一文件名
    const filename = blobInfo ? blobInfo.filename() : file.name;
    const fileKey = `editor/${Date.now()}_${Math.random()
      .toString(36)
      .slice(2)}_${sanitizeFileName(filename)}`;

    // 上传文件
    const formData = new FormData();
    formData.append("token", qiniuToken.value);
    formData.append("key", fileKey);
    formData.append("file", file);

    // 显示上传进度
    const loading = ElLoading.service({
      lock: true,
      text: "图片上传中...",
      background: "rgba(0, 0, 0, 0.7)",
    });

    try {
      const response = await axios.post(QINIU_UPLOAD_URL, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data?.key) {
        const finalFileUrl = `${QINIU_CDN_URL}/${response.data.key}`;
        if (!isValidUrl(finalFileUrl)) throw new Error("生成的图片地址无效");
        return finalFileUrl;
      } else {
        throw new Error(response.data?.error || "上传失败: 无效的响应");
      }
    } finally {
      loading.close();
    }
  } catch (error) {
    console.error("图片上传失败:", error);
    throw new Error(`图片上传失败: ${error.message || "请重试"}`);
  }
};

// TinyMCE 配置
const editorConfig = {
  height: 400,
  menubar: "edit insert view format table tools",
  plugins: [
    "advlist",
    "autolink",
    "lists",
    "link",
    "image",
    "charmap",
    "preview",
    "anchor",
    "searchreplace",
    "visualblocks",
    "code",
    "fullscreen",
    "insertdatetime",
    "media",
    "table",
    "help",
    "wordcount",
    "autosave",
    "quickbars",
  ],
  toolbar:
    "table tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | " +
    "tableinsertcolbefore tableinsertcolafter tabledeletecol | fullscreen preview fontsizeselect customFontSize " +
    "undo redo | formatselect | fontselect styleselect | bold italic underline strikethrough superscript subscript forecolor backcolor | " +
    "alignleft aligncenter alignright | bullist numlist outdent indent | link image | removeformat | help",
  toolbar_mode: "wrap",
  content_style: `
    body { font-family: "PingFang SC", Arial, sans-serif; font-size: 16px; line-height: 1.6; }
  `,
  base_url: "/tinymce",
  suffix: ".min",
  language: "zh_CN",
  license_key: "gpl",
  autosave_interval: "30s",
  autosave_retention: "20m",
  quickbars_selection_toolbar: "bold italic | quicklink h2 h3 blockquote",
  quickbars_insert_toolbar: "quickimage quicktable",
  font_formats:
    "默认=PingFang SC, Arial, sans-serif;宋体=SimSun;黑体=SimHei;微软雅黑=Microsoft YaHei;Arial=Arial;Times New Roman=Times New Roman",
  fontsize_formats:
    "8px 9px 10px 11px 12px 13px 14px 15px 16px 18px 20px 22px 24px 26px 28px 30px 32px 36px 48px 72px",
  style_formats: [
    {
      title: "文本颜色",
      items: [
        { title: "红色", inline: "span", styles: { color: "#ff0000" } },
        { title: "蓝色", inline: "span", styles: { color: "#0000ff" } },
        { title: "黑色", inline: "span", styles: { color: "#000000" } },
      ],
    },
    {
      title: "背景颜色",
      items: [
        {
          title: "黄色",
          inline: "span",
          styles: { backgroundColor: "#ffff00" },
        },
        {
          title: "绿色",
          inline: "span",
          styles: { backgroundColor: "#00ff00" },
        },
        {
          title: "白色",
          inline: "span",
          styles: { backgroundColor: "#ffffff" },
        },
      ],
    },
    {
      title: "字体大小",
      items: [
        { title: "小号", inline: "span", styles: { "font-size": "12px" } },
        { title: "中号", inline: "span", styles: { "font-size": "16px" } },
        { title: "大号", inline: "span", styles: { "font-size": "24px" } },
      ],
    },
  ],
  image_dimensions: false,
  image_description: true,
  automatic_uploads: true,
  relative_urls: false,
  remove_script_host: false,
  images_upload_handler: (blobInfo) =>
    new Promise((resolve, reject) => {
      uploadToQiniu(blobInfo.blob(), blobInfo)
        .then((finalFileUrl) => {
          resolve(finalFileUrl);
        })
        .catch((error) => {
          reject(error.message);
          ElMessage.error(error.message);
        });
    }),
  images_reuse_filename: false,
  table_default_attributes: {
    class: "my-table",
    border: "1",
  },
  table_cell_default_attributes: {
    style: "border: 1px solid #ddd; padding: 8px; vertical-align: top;",
  },
  table_default_styles: {
    "border-collapse": "collapse",
    width: "100%",
    border: "1px solid #ddd",
  },
  table_advtab: true, // 启用高级表格选项（可选）
  table_cell_advtab: true, // 启用单元格高级选项（可选）
  table_row_advtab: true, // 启用行高级选项（可选）
  setup: (editor) => {
    editor.ui.registry.addMenuButton("customFontSize", {
      text: "自定义字体大小",
      icon: "fontsize",
      fetch: (callback) => {
        const items = [
          {
            type: "menuitem",
            text: "输入字体大小",
            onAction: () => {
              const size = prompt("请输入字体大小（例如 25px）：");
              if (size && /^\d+px$/.test(size)) {
                editor.execCommand("FontSize", false, size);
              } else {
                alert("请输入有效的像素值，例如 25px");
              }
            },
          },
        ];
        callback(items);
      },
    });

    editor.on("init", () => {
      editor.setContent(content.value);
    });
  },
};

// URL 验证方法
const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// 更新内容
const updateContent = debounce(() => {
  emit("update:modelValue", content.value);
}, 300);

// 发布内容
const publishContent = () => {
  emit("publish", content.value);
};

// 触发手动上传
const triggerUpload = () => {
  imageInput.value.click();
};

// 处理手动上传
const handleImageUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  try {
    const finalFileUrl = await uploadToQiniu(file);
    if (window.tinymce?.activeEditor) {
      window.tinymce.activeEditor.insertContent(
        `<img src="${finalFileUrl}" alt="上传的图片" />`
      );
      ElMessage.success("图片上传成功");
    }
  } catch (error) {
    ElMessage.error(error.message || "图片上传失败，请重试");
  } finally {
    event.target.value = ""; // 清除文件选择
  }
};

// 监听 modelValue 变化
watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal !== content.value) {
      content.value = newVal;
      if (window.tinymce?.activeEditor) {
        window.tinymce.activeEditor.setContent(newVal);
      }
    }
  },
  { immediate: true }
);

// 监听内容变化
watch(content, (newVal) => {
  emit("update:modelValue", newVal);
});

// 组件挂载
onMounted(async () => {
  try {
    await initQiniu();
  } catch {
    ElMessage.error("上传功能初始化失败，请刷新页面");
  }
});

// 组件卸载
onBeforeUnmount(() => {
  if (window.tinymce?.activeEditor) {
    window.tinymce.activeEditor.remove();
  }
});
</script>

<style scoped>
.rich-text-editor {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.editor-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 10px;
  background: #f5f7fa;
  border-top: 1px solid #dee2e6;
}

.publish-btn {
  background: #00c4b4;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  text-transform: uppercase;
  font-weight: 600;
}

.publish-btn:hover {
  background: #00b3a4;
}

.upload-btn {
  background: #e0e0e0;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
}

.upload-btn:hover {
  background: #d0d0d0;
}
</style>
